import { z } from 'zod'
import { BacktestSimMode } from '../../enums/backtest-sim-mode.enum'
import { DEFAULT_TBANK_COMMISSION_RATE } from '../../constants/investments'
import {
  factorWeightsSchema,
  factorWeightsOverrideSchema,
  DEFAULT_FACTOR_WEIGHTS,
} from '../../investments/dip-pipeline/dip-factors.schema'

// strategy mode: 'manual' runs the fixed drop/shift/rise ladder (unchanged); 'minSpread'
// derives every mechanical parameter (drop/shift/rise/sell-step/sizing) from minSpreadPct +
// 30-day volatility + the capacity caps (maxHeldLots per instrument, budget as circuit-breaker).
export const dayTradeModeSchema = z.enum(['manual', 'minSpread'])
export type DayTradeMode = z.infer<typeof dayTradeModeSchema>

// plain object (kept un-refined) so `.shape` stays accessible for the per-instrument
// override-shape derivation in day-trade-bot.schema.ts; the exported daySettingsSchema below
// wraps it with the min-spread cross-field refinement.
export const daySettingsObject = z.object({
  dropPct: z.number().positive().max(100),
  shiftPct: z.number().positive().max(100),
  risePct: z.number().positive().max(100),
  sellShiftPct: z.number().positive().max(100),
  confirmationTicks: z.number().int().min(1),
  confirmationSeconds: z.number().int().min(0),
  lotsPerStep: z.number().int().min(1),
  maxLotsPerBuy: z.number().int().min(1),
  maxLotsPerSell: z.number().int().min(1),
  // hard cap on total lots held at once; the bot cannot buy beyond this until it
  // sells back down (default is set high so it effectively never binds)
  maxHeldLots: z.number().int().min(1),
  averageDown: z.boolean(),
  // when true, any lots still open at the end of a UTC day are force-sold at that
  // day's last close — positions never carry overnight (intraday-only trading).
  // defaulted so older stored configs/presets that predate the field stay valid.
  closeLotsSameDay: z.boolean().default(false),
  // when true, every sell targets risePct above the held position's average cost
  // (no lastSellPrice chaining) and scales out one lot per confirmed pullback;
  // sellShiftPct is ignored. defaulted so older stored configs/presets stay valid.
  sellFromAverage: z.boolean().default(false),
  commissionRate: z.number().min(0).max(1),
  spreadCushion: z.number().min(0).max(1),
  // max open exposure (capital committed to open positions) in the account currency. for the
  // live day-trade bot this is a SHARED, account-level pool: the combined committed cost across
  // every instrument under the account is kept within this single cap (a buy is sized down — or
  // skipped — so the account's total committed never exceeds it), which is why it is not
  // overridable per instrument. a single-instrument backtest holds only one position, so the cap
  // applies to it alone. 0 = no money cap (exposure bounded by maxHeldLots only). defaulted so
  // older configs stay valid.
  budget: z.number().min(0).default(0),
  // dip-pipeline band: the floor stays dropPct/shiftPct; the *Max widen the band so
  // factor contributions can move the effective percent within [floor, max]. default
  // 0 means "no band" — treated as floor-only (the session collapses max to the floor
  // whenever *Max <= floor), so 0 is a valid "no band" sentinel (min 0, not positive).
  dropPctMax: z.number().min(0).max(100).default(0),
  shiftPctMax: z.number().min(0).max(100).default(0),
  factorWeights: factorWeightsSchema.default(DEFAULT_FACTOR_WEIGHTS),
  moodRate: z.number().default(0),
  desireRate: z.number().default(0),
  // strategy mode; defaults to manual so every stored config that predates this field stays valid.
  mode: dayTradeModeSchema.default('manual'),
  // min-spread mode's single strategy input: the minimum net profit % (after both commissions and
  // tax) any lot may be sold for. required when mode === 'minSpread' (enforced by the refine below).
  // may be zero or negative — a non-positive floor lets the bot exit at breakeven/loss to free up
  // cash (the "sell-only" wind-down pairs with this to accelerate a money-back exit).
  minSpreadPct: z.number().min(-100).max(100).optional(),
})

// mode === 'minSpread' requires a minSpreadPct; applied to the exported schema only, so the
// plain daySettingsObject above keeps its `.shape` for the override-shape derivation.
const refineMinSpread = (data: z.infer<typeof daySettingsObject>, ctx: z.RefinementCtx): void => {
  if (data.mode === 'minSpread' && data.minSpreadPct == null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['minSpreadPct'],
      message: 'minSpreadPct is required in min-spread mode',
    })
  }
}

export const daySettingsSchema = daySettingsObject.superRefine(refineMinSpread)

export type DaySettingsDto = z.infer<typeof daySettingsSchema>

// strip a Zod `.default()` wrapper so an omitted per-instrument override key stays omitted
// (= inherit the account/backtest default) instead of snapping to the schema default on parse.
// shared by the live day-trade override (day-trade-bot.schema.ts) and the backtest override below.
export const stripDefault = (schema: z.ZodTypeAny): z.ZodTypeAny =>
  schema instanceof z.ZodDefault ? (schema.removeDefault() as unknown as z.ZodTypeAny) : schema

// canonical defaults for a day-trading strategy; shared so the backtest form
// and the sweep research page use identical starting values and cannot drift
export const DEFAULT_DAY_SETTINGS: DaySettingsDto = {
  dropPct: 1,
  shiftPct: 1,
  risePct: 2,
  sellShiftPct: 1,
  confirmationTicks: 1,
  confirmationSeconds: 0,
  lotsPerStep: 1,
  maxLotsPerBuy: 5,
  maxLotsPerSell: 5,
  maxHeldLots: 1000,
  averageDown: true,
  closeLotsSameDay: false,
  sellFromAverage: false,
  commissionRate: DEFAULT_TBANK_COMMISSION_RATE,
  spreadCushion: 0.0005,
  budget: 0,
  dropPctMax: 0,
  shiftPctMax: 0,
  factorWeights: DEFAULT_FACTOR_WEIGHTS,
  moodRate: 0,
  desireRate: 0,
  mode: 'manual',
}

// per-instrument override for a backtest: any subset of the strategy settings, each optional and
// merged over `defaults` per instrument. derived from daySettingsObject.shape (single source) the
// same way day-trade-bot.schema.ts derives its live override — adding a strategy knob to
// daySettingsObject now flows here automatically instead of needing a synchronized re-declaration
// (previously all 23 fields were copy-pasted here with their bounds). two deltas from the live
// override: budget IS kept (a single-instrument backtest holds one position, so the account-shared
// pool reason not to override it doesn't apply), and factorWeights uses the partial override schema
// (a backtest may tune a subset of weights).
// the dynamic Object.fromEntries erases the per-field static types, so re-assert the precise shape
// (each day-setting field mapped to its own value type, factorWeights to the partial override) so
// `.partial()`/`.extend()`/`.omit()` keep working and z.infer stays precise for buildWorkerPayload.
const backtestInstrumentOverrideShape = Object.fromEntries(
  Object.entries(daySettingsObject.shape).map(([key, field]) => [
    key,
    key === 'factorWeights'
      ? factorWeightsOverrideSchema
      : stripDefault(field as unknown as z.ZodTypeAny),
  ])
) as unknown as {
  [K in keyof Omit<DaySettingsDto, 'factorWeights'>]: z.ZodType<DaySettingsDto[K]>
} & {
  factorWeights: typeof factorWeightsOverrideSchema
}

export const backtestInstrumentSchema = z
  .object(backtestInstrumentOverrideShape)
  .partial()
  .extend({ instrumentUid: z.string() })

export type BacktestInstrumentDto = z.infer<typeof backtestInstrumentSchema>

// 21y cap leaves headroom so a "last 20 years" preset (end-of-day minus 20y,
// which can run a day or two over a flat 20y due to leap days) still validates
const MAX_SPAN_MS = 21 * 365.25 * 24 * 60 * 60 * 1000

export const backtestConfigSchema = z
  .object({
    defaults: daySettingsSchema,
    instruments: z.array(backtestInstrumentSchema).min(1).max(50),
    dateRange: z.object({
      from: z.coerce.date(),
      to: z.coerce.date(),
    }),
    simMode: z.nativeEnum(BacktestSimMode).default(BacktestSimMode.OHLC_PESSIMISTIC),
    bridgeTicksPerBar: z.number().int().min(2).max(200).default(20),
    // name of the saved preset this run's strategy settings came from, recorded
    // at run time and cleared if any setting was edited after applying. null when
    // the run used custom settings.
    presetName: z.string().max(60).nullable().optional(),
  })
  .refine((d) => d.dateRange.from <= d.dateRange.to, {
    message: 'dateRange.from must be <= dateRange.to',
    path: ['dateRange', 'from'],
  })
  .refine((d) => d.dateRange.to.getTime() - d.dateRange.from.getTime() <= MAX_SPAN_MS, {
    message: 'date range must not exceed 21 years',
    path: ['dateRange'],
  })

export type BacktestConfigDto = z.infer<typeof backtestConfigSchema>
